// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { AvailabilityMessageType, ProductModel } from "@insite/client-framework/Types/ApiModels";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import {
    CustomEventData,
    DocumentGroup,
    ProductPromotionMessage,
    RegionalInventory,
    VideoDocumentModel,
} from "@mcmc/App/Store/Reducers/ProductDetailsReducer";

export function getProductVideos(state: McMcApplicationState, product: ProductModel): VideoDocumentModel[] | undefined {
    if (product) {
        const customProductState = state.xProductDetails.byId[product.id];
        if (customProductState) {
            return customProductState.videos;
        }
    }
}

export function getSelectedVideoIndex(state: McMcApplicationState, product: ProductModel) {
    let selectedVideoIndex = -1;
    if (product) {
        const customProductState = state.xProductDetails.byId[product.id];
        if (customProductState) {
            selectedVideoIndex = customProductState.selectedVideoIndex;
        }
    }
    return selectedVideoIndex;
}

export function getSelectedVideo(state: McMcApplicationState, product: ProductModel): VideoDocumentModel | undefined {
    if (product) {
        const customProductState = state.xProductDetails.byId[product.id];
        if (customProductState && customProductState.selectedVideoIndex > -1) {
            return customProductState.videos[customProductState.selectedVideoIndex];
        }
    }
}

export function getSpecSheetLink(state: McMcApplicationState, product: ProductModel): string | undefined {
    if (product) {
        const customProductState = state.xProductDetails.byId[product.id];
        return customProductState && customProductState.specSheetLink;
    }
}

export function getProductDocumentGroups(
    state: McMcApplicationState,
    product: ProductModel,
): DocumentGroup[] | undefined {
    if (product) {
        const customProductState = state.xProductDetails.byId[product.id];
        return customProductState && customProductState.documentGroups;
    }
}

export function getPromotionMessage(
    state: McMcApplicationState,
    product: ProductModel,
): ProductPromotionMessage | undefined {
    if (product) {
        const customProductState = state.xProductDetails.byId[product.id];
        return customProductState && customProductState.promotionMessage;
    }
}

export function getIsEvent(state: McMcApplicationState, product: ProductModel): boolean | undefined {
    if (product) {
        const customProductState = state.xProductDetails.byId[product.id];
        return customProductState && customProductState.isEvent;
    }
}

export function getEventData(state: McMcApplicationState, product: ProductModel): CustomEventData | undefined {
    if (product) {
        const customProductState = state.xProductDetails.byId[product.id];
        return customProductState && customProductState.eventData;
    }
}

export function getRegionalInventory(
    state: McMcApplicationState,
    product: ProductModel,
): RegionalInventory | undefined {
    if (product) {
        const customProductState = state.xProductDetails.byId[product.id];
        return customProductState && customProductState.regionalInventory;
    }
}

export function hasEnoughSeats(productContext: ProductContextModel) {
    const {
        product,
        productInfo: { inventory, unitOfMeasure },
    } = productContext;
    const inventoryAvailability = inventory?.inventoryAvailabilityDtos?.find(o => o.unitOfMeasure === unitOfMeasure);

    return (
        product.isVariantParent ||
        !product.trackInventory ||
        inventoryAvailability?.availability?.messageType !== AvailabilityMessageType.OutOfStock
    );
}

export function getQuickShipIconUrl(state: McMcApplicationState, product: ProductModel): string | undefined {
    if (product) {
        const customProductState = state.xProductDetails.byId[product.id];
        return customProductState && customProductState.quickShipIconUrl;
    }
}
